import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useWorkflowExecution } from "../../context/WorkflowExecutionContext";
import { Button } from "@shadcn-in-nx/ui/button";
// import { Textarea } from "@shadcn-in-nx/ui/textarea";
import { useBluelagoon } from "@bluelagoon";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Input } from "@shadcn-in-nx/ui/input";
import AuthModal from "./AuthModal";
import {
    Send,
    Paperclip,
    X,
    Check,
    Globe,
    Loader2,
    ChevronDown,
    ChevronUp,
    ChevronRight,
    Zap,
    Square,
    Database,
    TableIcon,
    AlertCircle,
    Info,
    Brain,
    Sparkles,
    UserCheck,
    Plus,
    MoreHorizontal,
    MousePointer,
    FileCode,
    Folder,
    File as FileIcon,
    Search,
    Puzzle,
} from "lucide-react";
import HumanSupportTutorial from "./HumanSupportTutorial";
import HumanSupportPaywall from "./HumanSupportPaywall";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubTrigger,
    DropdownMenuSubContent,
} from "@shadcn-in-nx/ui/dropdown-menu";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useChatContext } from "../ChatContext";
import Icon from "../../components/Icon";
import { useParams, useNavigate } from "react-router-dom";
import { useWorkflow } from "../../context/WorkflowContext";
import { useWorkflowGeneration } from "../../context/WorkflowGenerationContext";
import MentionInput from "../../Apps/Workflow/components/WorkflowChatInput/MentionedInput";
import ComponentRefChip from "./ComponentRefChip";
import { SKILLS_AVAILABLE } from "../../skills";
import { INTEGRATION_TYPES } from "../../Integrations/Integrations";
import { useAuth } from "../../context/AuthContext";

const ChatInput = ({
    onSendMessage,
    isTyping,
    cancelBackgroundTask,
    isGeneratingCode,
    handleFileSelect,
    disabled = false,
    initialMessage = "",
    onMessageSet,
    minHeight,
    mode = "code_generation",
    setMode,
    setForceModel,
    forceModel,
    placeholderOverride,
    messagePrefix,
}) => {
    const { t } = useTranslation();
    const {
        user,
        setShowAuthModal,
        isInTrial,
        companyAgentUICredit,
        handleGetCompanyAgentUICredit,
        isLoadingGetCompanyAgentUICredit,
        isSupport,
        isSuperAdmin,
    } = useAuth();

    const { call: markTutorialComplete } = useBluelagoon({
        method: "markTutorialComplete",
    });
    const navigate = useNavigate();

    const limitReached = isLoadingGetCompanyAgentUICredit
        ? false
        : companyAgentUICredit !== -1 &&
          companyAgentUICredit <= 0.5 &&
          user?.id;

    const [message, setMessage] = useState(() => {
        const messageData = window.localStorage.getItem("messageData");

        if (
            messageData &&
            messageData !== "undefined" &&
            messageData !== null &&
            messageData !== ""
        ) {
            const count =
                parseInt(
                    window.localStorage.getItem("messageDataCount") || "0",
                    10
                ) + 1;

            if (count > 2) {
                window.localStorage.removeItem("messageData");
                window.localStorage.removeItem("messageDataCount");
            } else {
                window.localStorage.setItem(
                    "messageDataCount",
                    count.toString()
                );
                return JSON.parse(messageData);
            }
        }

        // Check query string for "query" parameter if no localStorage data
        const urlParams = new URLSearchParams(window.location.search);
        const queryParam = urlParams.get("query");

        if (queryParam) {
            return queryParam;
        }

        return "";
    });

    const [isDragOver, setIsDragOver] = useState(false);
    const [showAllFiles, setShowAllFiles] = useState(false);
    const prefixRef = useRef(null);
    const [prefixWidth, setPrefixWidth] = useState(0);

    useEffect(() => {
        if (prefixRef.current) {
            setPrefixWidth(prefixRef.current.offsetWidth);
        } else {
            setPrefixWidth(0);
        }
    }, [messagePrefix]);

    // + dropdown search states
    const [componentSearchTerm, setComponentSearchTerm] = useState("");
    const [skillsSearchTerm, setSkillsSearchTerm] = useState("");
    const [integrationsSearchTerm, setIntegrationsSearchTerm] = useState("");

    const filteredIntegrations = useMemo(() => {
        const q = integrationsSearchTerm.toLowerCase();
        return INTEGRATION_TYPES.filter(
            (i) =>
                (i.name || "").toLowerCase().includes(q) ||
                (i.category || "").toLowerCase().includes(q)
        );
    }, [integrationsSearchTerm]);

    // Human support state
    const [showHumanTutorial, setShowHumanTutorial] = useState(false);
    const [showHumanPaywall, setShowHumanPaywall] = useState(false);

    // Get existing ticket status from context
    const { hasHumanSupportTicket } = useWorkflowGeneration();

    // Check ticket count from user tutorials metadata
    const humanSupportTicketCount =
        user?.tutorials?.humanSupport?.metadata?.ticketCount || 0;

    // Check if user has a support subscription (bypasses limit)
    // Support subscriptions are in supportSubscriptions, not subscriptions
    const hasSupportSubscription =
        user?.company?.stripeCustomer?.supportSubscriptions?.some(
            (s) => s.status === "ACTIVE"
        );
    const hasReachedHumanSupportLimit =
        humanSupportTicketCount >= 2 && !hasSupportSubscription;

    // Check tutorial completion from user object first, then localStorage fallback
    const hasSeenHumanTutorial =
        user?.tutorials?.humanSupport?.completed ||
        localStorage.getItem("hasSeenHumanSupportTutorial") === "true";

    const handleHumanModeClick = () => {
        // If there's an existing open ticket, go straight to it (no tutorial/paywall)
        if (hasHumanSupportTicket) {
            setMode && setMode("human_support");
            return;
        }

        // No existing ticket - check tutorial first
        if (!hasSeenHumanTutorial) {
            setShowHumanTutorial(true);
            return;
        }

        // Tutorial done, check limit for NEW tickets only
        if (hasReachedHumanSupportLimit) {
            setShowHumanPaywall(true);
            return;
        }

        setMode && setMode("human_support");
    };

    const handleHumanTutorialContinue = async () => {
        // Mark tutorial as complete in localStorage (immediate)
        localStorage.setItem("hasSeenHumanSupportTutorial", "true");
        setShowHumanTutorial(false);

        // Persist to user profile via API (markTutorialComplete always sets completed: true)
        try {
            await markTutorialComplete({
                tutorialKey: "humanSupport",
            });
        } catch (error) {
            console.error(
                "Failed to save human support tutorial completion:",
                error
            );
        }

        // Check limit for NEW tickets only (existing tickets already handled above)
        if (hasReachedHumanSupportLimit) {
            setShowHumanPaywall(true);
            return;
        }
        setMode && setMode("human_support");
    };

    // Handle initial message from prompt cards
    useEffect(() => {
        if (initialMessage) {
            setMessage(initialMessage);
            onMessageSet && onMessageSet();
        }
    }, [initialMessage, onMessageSet]);

    // Database and table selection states
    const [selectedDatabaseForTables, setSelectedDatabaseForTables] =
        useState(null);
    const [showTableDropdown, setShowTableDropdown] = useState(false);
    const [tableDropdownPosition, setTableDropdownPosition] = useState({
        top: 0,
        left: 0,
    });

    const fileInputRef = useRef(null);

    // check if path name includes /chat/:id

    const { workflowId: workflowIdFromParams } = useParams();
    const { recentDatabases, handleResetRecentDatabases } =
        useWorkflowExecution();

    const {
        files: uploaderFiles,
        removeFile,
        uploadFiles,
        isLoadingFileUpload,
        selectedIntegration,
        clearSelectedIntegration,
        selectedSkills,
        removeSkill,

        isCreatingWorkflow,
        handleDeleteFile,
        handleMakePublic,
        isTransferring,
        stagedFiles,
        removeStagedFile,

        // Local staged files (not uploaded yet)
        localStagedFiles,
        stageFilesLocally,
        removeLocalStagedFile,
        workflow: contextWorkflow,
        componentRef,
        clearComponentRef,
        setComponentRef,
        toggleSelectMode,
        components,
        addSkill,
        selectIntegration,
    } = useChatContext();

    const { workflow, workflowId } = useWorkflow();
    const isAppV2 = workflow?.workflowType === "APP_V2";
    const showModeAndModel = isAppV2 || workflow?.workflowType === "AUTOMATION";
    const filteredComponents = useMemo(() => {
        if (!components?.length) return { pages: [], comps: [] };
        const q = componentSearchTerm.toLowerCase();
        const filtered = components.filter(
            (c) =>
                (c.name || "").toLowerCase().includes(q) ||
                (c.filePath || "").toLowerCase().includes(q)
        );
        return {
            pages: filtered.filter((c) => c.type === "PAGE"),
            comps: filtered.filter((c) => c.type !== "PAGE"),
        };
    }, [components, componentSearchTerm]);

    const filteredSkills = useMemo(() => {
        const q = skillsSearchTerm.toLowerCase();
        return SKILLS_AVAILABLE.filter((s) => !isAppV2 || !s.isTrigger).filter(
            (s) =>
                (s.name || "").toLowerCase().includes(q) ||
                (s.description || "").toLowerCase().includes(q)
        );
    }, [skillsSearchTerm, isAppV2]);

    const files = stagedFiles;

    useEffect(() => {
        uploaderFiles.forEach((file) => {
            if (file.status === "error" && file.error) {
                toast.error(file.error);
                // removeFile(file.id);
            }
        });
    }, [uploaderFiles, removeFile]);

    const handleSendMessage = () => {
        if (
            !message.trim() &&
            selectedSkills.length === 0 &&
            files.length === 0 &&
            localStagedFiles.length === 0
        )
            return;
        if (!user) {
            setShowAuthModal(true);
            window.localStorage.setItem("messageData", JSON.stringify(message));
            window.localStorage.setItem("messageDataCount", "0");
            return;
        }
        if (limitReached) {
            return;
        }

        const fullText = messagePrefix
            ? `${messagePrefix}: ${message}`
            : message;
        const messageData = {
            text: fullText,
            integration: selectedIntegration,
            skills: selectedSkills,
            files: files || [],
        };

        onSendMessage(messageData);
        setMessage("");
        // Note: clearStagedFiles is called in ChatMainInput after uploads complete
        window.localStorage.removeItem("messageData");
        window.localStorage.removeItem("messageDataCount");
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);

        const droppedFiles = Array.from(e.dataTransfer.files);
        if (droppedFiles.length > 0) {
            // Create a fake event object that matches what handleFileSelect expects
            const fakeEvent = {
                target: {
                    files: droppedFiles,
                },
            };
            handleFileSelect(fakeEvent);
        }
    };

    const handlePaste = React.useCallback(
        (e) => {
            const items = e.clipboardData?.items;
            if (!items) return;

            const pastedFiles = [];
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                if (item.kind === "file") {
                    const file = item.getAsFile();
                    if (file) {
                        pastedFiles.push(file);
                    }
                }
            }

            if (pastedFiles.length > 0) {
                e.preventDefault();
                // Create a fake event object that matches what handleFileSelect expects
                const fakeEvent = {
                    target: {
                        files: pastedFiles,
                    },
                };
                handleFileSelect(fakeEvent);
            }
        },
        [handleFileSelect]
    );

    // Add paste event listener for file upload
    useEffect(() => {
        document.addEventListener("paste", handlePaste);
        return () => {
            document.removeEventListener("paste", handlePaste);
        };
    }, [handlePaste]);

    const handleRemoveFile = (fileId) => {
        removeStagedFile(fileId);
    };

    const handleRemoveUploadingFile = (fileId) => {
        removeFile(fileId);
    };

    const handleMakeFilePublic = async (fileId) => {
        try {
            await handleMakePublic(fileId);
        } catch (error) {
            console.error("Failed to make file public:", error);
        }
    };

    const handleFileUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleDatabaseClick = (db, event) => {
        // If the database has more than 2 tables, show a dropdown to select a table
        if (db.tables && db.tables.length > 1) {
            setSelectedDatabaseForTables(db);
            setShowTableDropdown(true);

            // Calculate dropdown position based on the clicked element
            const rect = event.currentTarget.getBoundingClientRect();
            setTableDropdownPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
            });
        } else {
            // Just insert the database name
            const mention = `@[Database: ${db.name} Table: ${db.tables[0].name}]`;
            setMessage((prevMessage) => {
                const newMessage = prevMessage
                    ? `${prevMessage} ${mention}`
                    : mention;
                return newMessage;
            });
        }
    };

    const handleTableSelect = (table) => {
        const mention = `@[Database: ${selectedDatabaseForTables.name} Table: ${table.name}]`;
        setMessage((prevMessage) => {
            const newMessage = prevMessage
                ? `${prevMessage} ${mention}`
                : mention;
            return newMessage;
        });
        setShowTableDropdown(false);
        setSelectedDatabaseForTables(null);
    };

    const handleCloseTableDropdown = () => {
        setShowTableDropdown(false);
        setSelectedDatabaseForTables(null);
    };

    // Close dropdown when clicking outside or pressing Escape
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showTableDropdown) {
                // Check if click is outside the dropdown
                const dropdownElement = document.querySelector(
                    "[data-table-dropdown]"
                );
                if (
                    dropdownElement &&
                    !dropdownElement.contains(event.target)
                ) {
                    handleCloseTableDropdown();
                }
            }
        };

        const handleKeyDown = (event) => {
            if (showTableDropdown && event.key === "Escape") {
                handleCloseTableDropdown();
            }
        };

        if (showTableDropdown) {
            document.addEventListener("mousedown", handleClickOutside);
            document.addEventListener("keydown", handleKeyDown);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [showTableDropdown]);

    return (
        <>
            {!isAppV2 &&
                recentDatabases &&
                recentDatabases.workflowId !== workflowId &&
                recentDatabases.databases &&
                recentDatabases.databases.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg relative"
                    >
                        <div className="flex items-start gap-3">
                            <Database className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                                <h4 className="font-medium text-blue-900 mb-1">
                                    Recent Databases
                                </h4>
                                <p className="text-sm text-blue-700 mb-2">
                                    You've recently worked with these databases.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {recentDatabases.databases
                                        .slice(0, 3)
                                        .map((db, index) => (
                                            <Badge
                                                key={db.id || index}
                                                variant="secondary"
                                                className="bg-blue-100 text-blue-800 border-blue-200 cursor-pointer hover:bg-blue-200 transition-colors"
                                                onClick={(event) =>
                                                    handleDatabaseClick(
                                                        db,
                                                        event
                                                    )
                                                }
                                            >
                                                {db.name}
                                                {db.tables &&
                                                db.tables.length > 1 ? (
                                                    <span className="text-blue-600 ml-1">
                                                        (
                                                        {db.tables.length > 5
                                                            ? "5+"
                                                            : db.tables
                                                                  .length}{" "}
                                                        tables)
                                                    </span>
                                                ) : (
                                                    <span className="text-blue-600 ml-1">
                                                        (
                                                        {db.tables
                                                            .map(
                                                                (table) =>
                                                                    table.name
                                                            )
                                                            .join(", ")}
                                                    </span>
                                                )}
                                            </Badge>
                                        ))}
                                    {recentDatabases.databases.length > 3 &&
                                        recentDatabases.workflowId !==
                                            workflowId && (
                                            <Badge
                                                variant="secondary"
                                                className="bg-blue-100 text-blue-800 border-blue-200"
                                            >
                                                +
                                                {recentDatabases.databases
                                                    .length - 3}{" "}
                                                more
                                            </Badge>
                                        )}
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleResetRecentDatabases}
                                className="h-6 w-6 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-100"
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                    </motion.div>
                )}

            {/* Table Selection Dropdown */}
            {showTableDropdown && selectedDatabaseForTables && (
                <motion.div
                    data-table-dropdown
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="fixed bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto"
                    style={{
                        top: `${tableDropdownPosition.top}px`,
                        left: `${tableDropdownPosition.left}px`,
                        minWidth: "200px",
                    }}
                >
                    <div className="p-3 border-b border-gray-100 bg-blue-50">
                        <div className="flex items-center gap-2">
                            <Database className="h-4 w-4 text-blue-600" />
                            <span className="text-sm font-medium text-gray-700">
                                Select a table from{" "}
                                {selectedDatabaseForTables.name}
                            </span>
                            <button
                                onClick={handleCloseTableDropdown}
                                className="ml-auto text-gray-400 hover:text-red-500 transition-colors"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </div>
                    </div>
                    <div className="py-1">
                        {/* Option to select just the database */}
                        <div
                            onClick={() => {
                                const mention = `@[Database: ${selectedDatabaseForTables.name}]`;
                                setMessage((prevMessage) => {
                                    const newMessage = prevMessage
                                        ? `${prevMessage} ${mention}`
                                        : mention;
                                    return newMessage;
                                });
                                handleCloseTableDropdown();
                            }}
                            className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm border-b border-gray-100 transition-colors"
                        >
                            <div className="flex items-center gap-2">
                                <Database className="h-4 w-4 text-blue-600" />
                                <span className="text-gray-700 font-medium">
                                    Select Database Only
                                </span>
                            </div>
                        </div>

                        {/* Table options */}
                        {selectedDatabaseForTables.tables.map(
                            (table, index) => (
                                <div
                                    key={table.id || index}
                                    onClick={() => handleTableSelect(table)}
                                    className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm border-b border-gray-100 last:border-b-0 transition-colors"
                                >
                                    <div className="flex items-center gap-2">
                                        <TableIcon className="h-4 w-4 text-blue-600" />
                                        <span className="text-gray-700">
                                            {table.name}
                                        </span>
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                </motion.div>
            )}

            <motion.div
                key={workflowId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-white border rounded-xl shadow-lg mb-2 transition-all duration-300 ${
                    isDragOver
                        ? "border-blue-400 shadow-xl bg-blue-50"
                        : "border-gray-200"
                }`}
            >
                {/* Selected Integration Display */}
                {selectedIntegration && (
                    <div className="p-3 border-b border-gray-100 bg-blue-50">
                        <div className="flex items-center gap-2">
                            <Check className="h-4 w-4 text-green-600" />
                            <span className="text-sm font-medium text-gray-700">
                                {t("chatInput.usingIntegration")}
                            </span>
                            <div className="flex items-center gap-2">
                                {selectedIntegration.imageIcon ? (
                                    <img
                                        src={selectedIntegration.imageIcon}
                                        alt={selectedIntegration.name}
                                        className="h-4 w-4"
                                    />
                                ) : (
                                    <Icon
                                        name={selectedIntegration.icon}
                                        className="h-4 w-4"
                                    />
                                )}
                                <Badge variant="secondary" className="text-xs">
                                    {selectedIntegration.name}
                                </Badge>
                            </div>
                            <button
                                onClick={clearSelectedIntegration}
                                className="ml-auto text-gray-400 hover:text-red-500 transition-colors"
                                title={t("chatInput.clearIntegration")}
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Uploaded Files and Selected Skills Preview */}
                <AnimatePresence>
                    {(files?.length > 0 ||
                        selectedSkills.length > 0 ||
                        uploaderFiles?.some((f) => f.status !== "completed") ||
                        localStagedFiles?.length > 0) &&
                        (() => {
                            const allItems = [
                                ...selectedSkills.map((skill) => ({
                                    type: "skill",
                                    data: skill,
                                })),
                                // Local staged files (not uploaded yet)
                                ...(localStagedFiles || []).map((file) => ({
                                    type: "local",
                                    data: file,
                                })),
                                ...(files || []).map((file) => ({
                                    type: "file",
                                    data: file,
                                })),
                                ...(uploaderFiles || [])
                                    .filter(
                                        (file) => file.status !== "completed"
                                    )
                                    .map((file) => ({
                                        type: "uploading",
                                        data: file,
                                    })),
                            ];
                            const totalItems = allItems.length;
                            const displayItems = showAllFiles
                                ? allItems
                                : allItems.slice(0, 4);
                            const hasMore = totalItems > 4;

                            const itemVariants = {
                                initial: { opacity: 0, scale: 0.8, y: -10 },
                                animate: { opacity: 1, scale: 1, y: 0 },
                                exit: { opacity: 0, scale: 0.8, y: -10 },
                            };

                            return (
                                <motion.div
                                    key="files-preview-container"
                                    className="p-2 border-b border-gray-100 overflow-hidden"
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{
                                        duration: 0.2,
                                        ease: "easeInOut",
                                    }}
                                >
                                    <div className="flex flex-wrap gap-2 items-center">
                                        <AnimatePresence mode="popLayout">
                                            {displayItems.map((item, index) => {
                                                if (item.type === "skill") {
                                                    const skill = item.data;
                                                    return (
                                                        <motion.div
                                                            key={`skill-${skill.id}`}
                                                            layout
                                                            variants={
                                                                itemVariants
                                                            }
                                                            initial="initial"
                                                            animate="animate"
                                                            exit="exit"
                                                            transition={{
                                                                duration: 0.2,
                                                                delay:
                                                                    index *
                                                                    0.03,
                                                            }}
                                                            className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1 text-sm"
                                                        >
                                                            <span className="text-blue-600 text-xs">
                                                                {skill.icon}
                                                            </span>
                                                            <span className="text-blue-700 font-medium truncate max-w-32">
                                                                {skill.name}
                                                            </span>
                                                            <button
                                                                onClick={() =>
                                                                    removeSkill(
                                                                        skill.id
                                                                    )
                                                                }
                                                                className="text-blue-400 hover:text-red-500 transition-colors"
                                                            >
                                                                <X className="h-3 w-3" />
                                                            </button>
                                                        </motion.div>
                                                    );
                                                }

                                                if (item.type === "file") {
                                                    const file = item.data;
                                                    const fileObj =
                                                        file?.file || file;
                                                    const fileId = fileObj?.id;
                                                    const fileName =
                                                        fileObj?.name;
                                                    const isPublic =
                                                        fileObj?.isPublic;

                                                    return (
                                                        <motion.div
                                                            key={`file-${fileId}`}
                                                            layout
                                                            variants={
                                                                itemVariants
                                                            }
                                                            initial="initial"
                                                            animate="animate"
                                                            exit="exit"
                                                            transition={{
                                                                duration: 0.2,
                                                                delay:
                                                                    index *
                                                                    0.03,
                                                            }}
                                                            className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1 text-sm"
                                                        >
                                                            <Paperclip className="h-3 w-3 text-gray-500" />
                                                            <span className="text-gray-700 truncate max-w-32">
                                                                {fileName}
                                                            </span>
                                                            <div className="flex items-center gap-1">
                                                                {/* Public button */}
                                                                {isPublic ? (
                                                                    <button
                                                                        className="text-green-500 cursor-default"
                                                                        title="This file is public"
                                                                    >
                                                                        <Globe className="h-3 w-3" />
                                                                    </button>
                                                                ) : (
                                                                    <button
                                                                        onClick={() =>
                                                                            handleMakeFilePublic(
                                                                                fileId
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            isTransferring
                                                                        }
                                                                        className="text-gray-400 hover:text-blue-500 transition-colors disabled:opacity-50"
                                                                        title="Make file public"
                                                                    >
                                                                        <Globe className="h-3 w-3" />
                                                                    </button>
                                                                )}
                                                                {/* Remove button */}
                                                                <button
                                                                    onClick={() =>
                                                                        handleRemoveFile(
                                                                            fileId
                                                                        )
                                                                    }
                                                                    className="text-gray-400 hover:text-red-500 transition-colors"
                                                                >
                                                                    <X className="h-3 w-3" />
                                                                </button>
                                                            </div>
                                                        </motion.div>
                                                    );
                                                }

                                                if (item.type === "local") {
                                                    // Local staged file (not uploaded yet)
                                                    const file = item.data;
                                                    return (
                                                        <motion.div
                                                            key={`local-${file.id}`}
                                                            layout
                                                            variants={
                                                                itemVariants
                                                            }
                                                            initial="initial"
                                                            animate="animate"
                                                            exit="exit"
                                                            transition={{
                                                                duration: 0.2,
                                                                delay:
                                                                    index *
                                                                    0.03,
                                                            }}
                                                            className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-1 text-sm"
                                                        >
                                                            {file.preview ? (
                                                                <img
                                                                    src={
                                                                        file.preview
                                                                    }
                                                                    alt={
                                                                        file.name
                                                                    }
                                                                    className="h-4 w-4 rounded object-cover"
                                                                />
                                                            ) : (
                                                                <Paperclip className="h-3 w-3 text-green-600" />
                                                            )}
                                                            <span className="text-green-700 truncate max-w-32">
                                                                {file.name}
                                                            </span>
                                                            <span className="text-green-500 text-xs">
                                                                Ready
                                                            </span>
                                                            <button
                                                                onClick={() =>
                                                                    removeLocalStagedFile(
                                                                        file.id
                                                                    )
                                                                }
                                                                className="text-green-400 hover:text-red-500 transition-colors"
                                                            >
                                                                <X className="h-3 w-3" />
                                                            </button>
                                                        </motion.div>
                                                    );
                                                }

                                                if (item.type === "uploading") {
                                                    const file = item.data;
                                                    return (
                                                        <motion.div
                                                            key={`uploading-${file.id}`}
                                                            layout
                                                            variants={
                                                                itemVariants
                                                            }
                                                            initial="initial"
                                                            animate="animate"
                                                            exit="exit"
                                                            transition={{
                                                                duration: 0.2,
                                                                delay:
                                                                    index *
                                                                    0.03,
                                                            }}
                                                            className={`flex items-center gap-2 rounded-lg px-3 py-1 text-sm ${
                                                                file.status ===
                                                                "error"
                                                                    ? "bg-red-50 border border-red-200"
                                                                    : "bg-blue-50 border border-blue-200"
                                                            }`}
                                                        >
                                                            {file.status ===
                                                            "uploading" ? (
                                                                <Loader2 className="h-3 w-3 text-blue-500 animate-spin" />
                                                            ) : file.status ===
                                                              "error" ? (
                                                                <AlertCircle className="h-3 w-3 text-red-500 flex-shrink-0" />
                                                            ) : (
                                                                <Paperclip className="h-3 w-3 text-gray-500" />
                                                            )}
                                                            <div className="flex flex-col min-w-0">
                                                                <span
                                                                    className={`truncate max-w-32 ${
                                                                        file.status ===
                                                                        "error"
                                                                            ? "text-red-700"
                                                                            : "text-blue-700"
                                                                    }`}
                                                                >
                                                                    {file.name}
                                                                </span>
                                                                {file.status ===
                                                                    "error" &&
                                                                    file.error && (
                                                                        <span className="text-red-500 text-xs truncate max-w-40">
                                                                            {
                                                                                file.error
                                                                            }
                                                                        </span>
                                                                    )}
                                                            </div>
                                                            <button
                                                                onClick={() =>
                                                                    handleRemoveUploadingFile(
                                                                        file.id
                                                                    )
                                                                }
                                                                className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                                                            >
                                                                <X className="h-3 w-3" />
                                                            </button>
                                                        </motion.div>
                                                    );
                                                }

                                                return null;
                                            })}
                                        </AnimatePresence>

                                        {/* Show All / Show Less Button */}
                                        {hasMore && (
                                            <motion.button
                                                layout
                                                onClick={() =>
                                                    setShowAllFiles(
                                                        !showAllFiles
                                                    )
                                                }
                                                className="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 rounded-lg px-3 py-1 text-sm text-gray-600 transition-colors"
                                                title={
                                                    showAllFiles
                                                        ? "Show less"
                                                        : `View all ${totalItems} items`
                                                }
                                            >
                                                {showAllFiles ? (
                                                    <>
                                                        <ChevronUp className="h-3 w-3" />
                                                        <span>Show less</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <ChevronDown className="h-3 w-3" />
                                                        <span>
                                                            +{totalItems - 4}{" "}
                                                            more
                                                        </span>
                                                    </>
                                                )}
                                            </motion.button>
                                        )}
                                    </div>
                                </motion.div>
                            );
                        })()}
                </AnimatePresence>

                {/* Code Generation Limit Warning */}
                {limitReached && (
                    <div
                        className={`p-3 border-b ${
                            limitReached
                                ? "bg-red-50 border-red-200"
                                : companyAgentUICredit <= 0.5
                                ? "bg-yellow-50 border-yellow-200"
                                : "bg-blue-50 border-blue-200"
                        }`}
                    >
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                                {limitReached ? (
                                    <AlertCircle className="h-4 w-4 text-red-600" />
                                ) : companyAgentUICredit <= 2 ? (
                                    <AlertCircle className="h-4 w-4 text-yellow-600" />
                                ) : (
                                    <Info className="h-4 w-4 text-blue-600" />
                                )}
                                <span className="text-sm font-medium">
                                    {limitReached
                                        ? t("chatInput.capacityDepleted")
                                        : companyAgentUICredit <= 2
                                        ? t("chatInput.capacityLow", {
                                              credits:
                                                  companyAgentUICredit.toFixed(
                                                      2
                                                  ),
                                          })
                                        : t("chatInput.capacityRemaining", {
                                              percent: Math.round(
                                                  (companyAgentUICredit / 200) *
                                                      100
                                              ),
                                          })}
                                </span>
                            </div>
                            <Button
                                size="sm"
                                onClick={() => {
                                    localStorage.setItem(
                                        "billing_return_url",
                                        window.location.pathname +
                                            window.location.search
                                    );
                                    navigate("/billing");
                                }}
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                            >
                                {t("chatInput.addCapacity")}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Component Reference Chip */}
                {componentRef && (
                    <div className="px-3 pt-2">
                        <ComponentRefChip
                            componentRef={componentRef}
                            onClear={clearComponentRef}
                        />
                    </div>
                )}

                {/* Chat Input Area */}
                <div
                    className={`p-2 transition-all duration-300 ${
                        isDragOver ? "bg-blue-50 border-blue-200 shadow-lg" : ""
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    {/* Textarea Container */}
                    <div
                        className={`flex-1 max-h-[500px] ${
                            minHeight
                                ? `min-h-[${minHeight}] `
                                : " min-h-[50px]"
                        } transition-all duration-300 ${
                            isDragOver
                                ? "bg-blue-50 border border-blue-200 rounded-lg"
                                : ""
                        }`}
                        style={{ position: "relative" }}
                    >
                        {messagePrefix && (
                            <span
                                ref={prefixRef}
                                className="absolute inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap select-none pointer-events-none z-10"
                                style={{
                                    backgroundColor: "rgba(124, 58, 237, 0.12)",
                                    color: "#7c3aed",
                                    top: 10,
                                    left: 10,
                                }}
                            >
                                {messagePrefix}
                            </span>
                        )}
                        <MentionInput
                            value={message}
                            onChange={setMessage}
                            onKeyPress={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSendMessage();
                                }
                            }}
                            disabled={
                                disabled ||
                                isLoadingFileUpload ||
                                isTyping ||
                                limitReached
                            }
                            inputStyle={
                                messagePrefix
                                    ? { textIndent: prefixWidth + 8 }
                                    : undefined
                            }
                            placeholder={
                                disabled
                                    ? "This workflow is archived and cannot be used"
                                    : isCreatingWorkflow
                                    ? t(
                                          "chatInput.creatingWorkflowPlaceholder",
                                          {
                                              defaultValue:
                                                  "Creating workspace...",
                                          }
                                      )
                                    : isTyping
                                    ? t("chatInput.codeGeneratingPlaceholder")
                                    : isDragOver
                                    ? t(
                                          "chatInput.dropFilesHereToUploadPlaceholder"
                                      )
                                    : placeholderOverride ||
                                      t(
                                          "chatInput.typeYourMessageHerePlaceholder"
                                      )
                            }
                        />
                    </div>

                    {/* Action Row - Mode, Add, More, Send */}
                    <div className="flex items-center gap-0.5 mt-2 min-w-0 overflow-hidden">
                        {/* Primary: Mode Dropdown */}
                        {showModeAndModel && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-gray-100 transition-colors text-xs font-medium text-gray-700 outline-none">
                                        {mode === "plan" ? (
                                            <>
                                                <Brain className="h-3 w-3 text-yellow-600" />
                                                <span>
                                                    {t(
                                                        "chatInput.modePlanning"
                                                    )}
                                                </span>
                                            </>
                                        ) : mode === "human_support" ? (
                                            <>
                                                <UserCheck className="h-3 w-3 text-green-600" />
                                                <span>
                                                    {t("chatInput.modeHuman")}
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <Zap className="h-3 w-3 text-blue-600" />
                                                <span>
                                                    {t("chatInput.modeBuild")}
                                                </span>
                                            </>
                                        )}
                                        <ChevronDown className="h-2.5 w-2.5 text-gray-400" />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    align="start"
                                    className="w-56"
                                >
                                    <DropdownMenuLabel className="text-xs">
                                        {t("chatInput.conversationMode")}
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        className="flex flex-col items-start gap-1 p-2 cursor-pointer"
                                        onClick={() =>
                                            setMode && setMode("plan")
                                        }
                                    >
                                        <div className="flex items-center gap-1.5 text-xs font-medium">
                                            <Brain className="h-3 w-3 text-yellow-600" />
                                            <span>
                                                {t("chatInput.modePlanning")}
                                            </span>
                                        </div>
                                        <p className="text-[10px] text-gray-500">
                                            {t(
                                                "chatInput.modePlanningDescription"
                                            )}
                                        </p>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        className="flex flex-col items-start gap-1 p-2 cursor-pointer"
                                        onClick={() =>
                                            setMode &&
                                            setMode("code_generation")
                                        }
                                    >
                                        <div className="flex items-center gap-1.5 text-xs font-medium">
                                            <Zap className="h-3 w-3 text-blue-600" />
                                            <span>
                                                {t("chatInput.modeBuild")}
                                            </span>
                                        </div>
                                        <p className="text-[10px] text-gray-500">
                                            {t(
                                                "chatInput.modeBuildDescription"
                                            )}
                                        </p>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        className="flex flex-col items-start gap-1 p-2 cursor-pointer"
                                        onClick={handleHumanModeClick}
                                    >
                                        <div className="flex items-center gap-1.5 text-xs font-medium">
                                            <UserCheck className="h-3 w-3 text-green-600" />
                                            <span>
                                                {t("chatInput.modeHuman")}
                                            </span>
                                        </div>
                                        <p className="text-[10px] text-gray-500">
                                            {t(
                                                "chatInput.modeHumanDescription"
                                            )}
                                        </p>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}

                        {/* Add: file upload, select element, components, skills, integrations */}
                        <DropdownMenu
                            onOpenChange={(open) => {
                                if (open) {
                                    setComponentSearchTerm("");
                                    setSkillsSearchTerm("");
                                    setIntegrationsSearchTerm("");
                                }
                            }}
                        >
                            <DropdownMenuTrigger asChild>
                                <button
                                    className="flex items-center gap-1 px-2 py-1 rounded-md hover:bg-gray-100 transition-colors text-xs font-medium text-gray-500 outline-none"
                                    disabled={disabled || isTyping}
                                >
                                    <Plus className="h-3.5 w-3.5" />
                                    <ChevronDown className="h-2.5 w-2.5 text-gray-400" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="start"
                                className="w-72 max-h-[70vh] overflow-y-auto"
                            >
                                <DropdownMenuItem
                                    className="flex items-center gap-2 cursor-pointer"
                                    onClick={handleFileUploadClick}
                                    disabled={
                                        disabled ||
                                        isLoadingFileUpload ||
                                        isTyping
                                    }
                                >
                                    <Paperclip className="h-3.5 w-3.5 text-gray-500" />
                                    <span className="text-xs">
                                        {t("chatInput.uploadFiles")}
                                    </span>
                                </DropdownMenuItem>
                                {isAppV2 && (
                                    <DropdownMenuItem
                                        className="flex items-center gap-2 cursor-pointer"
                                        onClick={() =>
                                            toggleSelectMode &&
                                            toggleSelectMode()
                                        }
                                        disabled={disabled || isTyping}
                                    >
                                        <MousePointer className="h-3.5 w-3.5 text-gray-500" />
                                        <span className="text-xs">
                                            {t("chatInput.selectElement")}
                                        </span>
                                    </DropdownMenuItem>
                                )}

                                {/* Skills Sub-menu */}
                                <DropdownMenuSub>
                                    <DropdownMenuSubTrigger className="flex items-center gap-2 cursor-pointer">
                                        <Sparkles className="h-3.5 w-3.5 text-gray-500" />
                                        <span className="text-xs">
                                            {t("chatInput.skills")}
                                        </span>
                                    </DropdownMenuSubTrigger>
                                    <DropdownMenuSubContent className="w-64 max-h-[50vh] overflow-y-auto">
                                        <div
                                            className="p-2"
                                            onKeyDown={(e) =>
                                                e.stopPropagation()
                                            }
                                        >
                                            <div className="relative">
                                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                                                <Input
                                                    value={skillsSearchTerm}
                                                    onChange={(e) =>
                                                        setSkillsSearchTerm(
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder={t(
                                                        "chatInput.searchSkills"
                                                    )}
                                                    className="pl-7 h-7 text-sm"
                                                />
                                            </div>
                                        </div>
                                        {filteredSkills.map((skill) => (
                                            <DropdownMenuItem
                                                key={skill.id}
                                                onClick={() =>
                                                    addSkill && addSkill(skill)
                                                }
                                                className="flex items-center gap-2 cursor-pointer p-2"
                                            >
                                                <span className="text-sm flex-shrink-0">
                                                    {skill.icon}
                                                </span>
                                                <div className="min-w-0 flex-1">
                                                    <p className="font-medium truncate text-xs">
                                                        {skill.name}
                                                    </p>
                                                </div>
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuSubContent>
                                </DropdownMenuSub>

                                {/* Integrations Sub-menu */}
                                <DropdownMenuSub>
                                    <DropdownMenuSubTrigger className="flex items-center gap-2 cursor-pointer">
                                        <Puzzle className="h-3.5 w-3.5 text-gray-500" />
                                        <span className="text-xs">
                                            {t("chatInput.integrations")}
                                        </span>
                                    </DropdownMenuSubTrigger>
                                    <DropdownMenuSubContent className="w-64 max-h-[50vh] overflow-y-auto">
                                        <div
                                            className="p-2"
                                            onKeyDown={(e) =>
                                                e.stopPropagation()
                                            }
                                        >
                                            <div className="relative">
                                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                                                <Input
                                                    value={
                                                        integrationsSearchTerm
                                                    }
                                                    onChange={(e) =>
                                                        setIntegrationsSearchTerm(
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder={t(
                                                        "chatInput.searchIntegrations"
                                                    )}
                                                    className="pl-7 h-7 text-sm"
                                                />
                                            </div>
                                        </div>
                                        {filteredIntegrations.map(
                                            (integration) => (
                                                <DropdownMenuItem
                                                    key={integration.id}
                                                    onClick={() =>
                                                        selectIntegration &&
                                                        selectIntegration(
                                                            integration
                                                        )
                                                    }
                                                    className="flex items-center gap-2 cursor-pointer p-2"
                                                >
                                                    {integration.imageIcon ? (
                                                        <img
                                                            src={
                                                                integration.imageIcon
                                                            }
                                                            alt={
                                                                integration.name
                                                            }
                                                            className="h-4 w-4 flex-shrink-0"
                                                        />
                                                    ) : (
                                                        <Icon
                                                            name={
                                                                integration.icon ||
                                                                "Puzzle"
                                                            }
                                                            className="h-4 w-4 flex-shrink-0"
                                                        />
                                                    )}
                                                    <span className="font-medium truncate text-xs">
                                                        {integration.name}
                                                    </span>
                                                </DropdownMenuItem>
                                            )
                                        )}
                                    </DropdownMenuSubContent>
                                </DropdownMenuSub>

                                {/* Reference Component Sub-menu (APP_V2 only) */}
                                {isAppV2 && components?.length > 0 && (
                                    <DropdownMenuSub>
                                        <DropdownMenuSubTrigger className="flex items-center gap-2 cursor-pointer">
                                            <FileCode className="h-3.5 w-3.5 text-gray-500" />
                                            <span className="text-xs">
                                                {t(
                                                    "chatInput.referenceComponent"
                                                )}
                                            </span>
                                        </DropdownMenuSubTrigger>
                                        <DropdownMenuSubContent className="w-64 max-h-[50vh] overflow-y-auto">
                                            <div
                                                className="p-2"
                                                onKeyDown={(e) =>
                                                    e.stopPropagation()
                                                }
                                            >
                                                <div className="relative">
                                                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                                                    <Input
                                                        value={
                                                            componentSearchTerm
                                                        }
                                                        onChange={(e) =>
                                                            setComponentSearchTerm(
                                                                e.target.value
                                                            )
                                                        }
                                                        placeholder={t(
                                                            "chatInput.searchComponents"
                                                        )}
                                                        className="pl-7 h-7 text-sm"
                                                    />
                                                </div>
                                            </div>
                                            {filteredComponents.pages.length >
                                                0 && (
                                                <>
                                                    <DropdownMenuLabel className="text-xs text-gray-400">
                                                        {t("chatInput.pages")}
                                                    </DropdownMenuLabel>
                                                    {filteredComponents.pages.map(
                                                        (item) => (
                                                            <DropdownMenuItem
                                                                key={item.id}
                                                                onClick={() =>
                                                                    setComponentRef(
                                                                        {
                                                                            componentId:
                                                                                item.id,
                                                                            componentName:
                                                                                item.name,
                                                                            filePath:
                                                                                item.filePath,
                                                                        }
                                                                    )
                                                                }
                                                                className={`flex items-center gap-2 cursor-pointer p-2 ${
                                                                    componentRef?.componentId ===
                                                                    item.id
                                                                        ? "bg-blue-50 text-blue-700"
                                                                        : ""
                                                                }`}
                                                            >
                                                                <Folder className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                                                                <span className="font-medium truncate text-xs">
                                                                    {item.name}
                                                                </span>
                                                            </DropdownMenuItem>
                                                        )
                                                    )}
                                                </>
                                            )}
                                            {filteredComponents.comps.length >
                                                0 && (
                                                <>
                                                    <DropdownMenuLabel className="text-xs text-gray-400">
                                                        {t(
                                                            "chatInput.components"
                                                        )}
                                                    </DropdownMenuLabel>
                                                    {filteredComponents.comps.map(
                                                        (item) => (
                                                            <DropdownMenuItem
                                                                key={item.id}
                                                                onClick={() =>
                                                                    setComponentRef(
                                                                        {
                                                                            componentId:
                                                                                item.id,
                                                                            componentName:
                                                                                item.name,
                                                                            filePath:
                                                                                item.filePath,
                                                                        }
                                                                    )
                                                                }
                                                                className={`flex items-center gap-2 cursor-pointer p-2 ${
                                                                    componentRef?.componentId ===
                                                                    item.id
                                                                        ? "bg-blue-50 text-blue-700"
                                                                        : ""
                                                                }`}
                                                            >
                                                                <FileIcon className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                                                                <span className="font-medium truncate text-xs">
                                                                    {item.name}
                                                                </span>
                                                            </DropdownMenuItem>
                                                        )
                                                    )}
                                                </>
                                            )}
                                        </DropdownMenuSubContent>
                                    </DropdownMenuSub>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {/* More: model selection, advanced */}
                        {showModeAndModel && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button className="flex items-center gap-1 px-2 py-1 rounded-md hover:bg-gray-100 transition-colors text-gray-500 outline-none">
                                        <MoreHorizontal className="h-3.5 w-3.5" />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    align="start"
                                    className="w-72"
                                >
                                    <DropdownMenuLabel className="text-xs">
                                        {t("chatInput.selectModel")}
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        className="flex flex-col items-start gap-1 p-2 cursor-pointer"
                                        onClick={() =>
                                            setForceModel &&
                                            setForceModel("auto")
                                        }
                                    >
                                        <div className="flex items-center gap-1.5 text-xs font-medium w-full">
                                            <Sparkles className="h-3 w-3 text-purple-600" />
                                            <span>
                                                {t("chatInput.modelAuto")}
                                            </span>
                                            {forceModel === "auto" && (
                                                <Check className="h-3 w-3 ml-auto text-purple-600" />
                                            )}
                                        </div>
                                        <p className="text-[10px] text-gray-500">
                                            {t(
                                                "chatInput.modelAutoDescription"
                                            )}
                                        </p>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        className="flex flex-col items-start gap-1 p-2 cursor-pointer"
                                        onClick={() =>
                                            setForceModel &&
                                            setForceModel(
                                                "us.anthropic.claude-opus-4-7"
                                            )
                                        }
                                    >
                                        <div className="flex items-center gap-1.5 text-xs font-medium w-full">
                                            <span>
                                                {t("chatInput.modelClaudeOpus")}
                                            </span>
                                            <Badge
                                                variant="secondary"
                                                className="text-[10px] px-1 py-0 bg-purple-100 text-purple-700"
                                            >
                                                {t("chatInput.recommended")}
                                            </Badge>
                                            {forceModel ===
                                                "us.anthropic.claude-opus-4-7" && (
                                                <Check className="h-3 w-3 ml-auto text-purple-600" />
                                            )}
                                        </div>
                                        <p className="text-[10px] text-gray-500">
                                            {t(
                                                "chatInput.modelClaudeOpusDescription"
                                            )}
                                        </p>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        className="flex flex-col items-start gap-1 p-2 cursor-pointer"
                                        onClick={() =>
                                            setForceModel &&
                                            setForceModel(
                                                "us.anthropic.claude-sonnet-4-6"
                                            )
                                        }
                                    >
                                        <div className="flex items-center gap-1.5 text-xs font-medium w-full">
                                            <span>
                                                {t(
                                                    "chatInput.modelClaudeSonnet46"
                                                )}
                                            </span>
                                            {forceModel ===
                                                "us.anthropic.claude-sonnet-4-6" && (
                                                <Check className="h-3 w-3 ml-auto text-purple-600" />
                                            )}
                                        </div>
                                        <p className="text-[10px] text-gray-500">
                                            {t(
                                                "chatInput.modelClaudeSonnet46Description"
                                            )}
                                        </p>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        className="flex flex-col items-start gap-1 p-2 cursor-pointer"
                                        onClick={() =>
                                            setForceModel &&
                                            setForceModel("gpt-5.5")
                                        }
                                    >
                                        <div className="flex items-center gap-1.5 text-xs font-medium w-full">
                                            <span>
                                                {t("chatInput.modelGpt55")}
                                            </span>
                                            <Badge
                                                variant="secondary"
                                                className="text-[10px] px-1 py-0 bg-green-100 text-green-700"
                                            >
                                                {t("chatInput.new")}
                                            </Badge>
                                            {forceModel === "gpt-5.5" && (
                                                <Check className="h-3 w-3 ml-auto text-purple-600" />
                                            )}
                                        </div>
                                        <p className="text-[10px] text-gray-500">
                                            {t(
                                                "chatInput.modelGpt55Description"
                                            )}
                                        </p>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        className="flex flex-col items-start gap-1 p-2 cursor-pointer"
                                        onClick={() =>
                                            setForceModel &&
                                            setForceModel("gpt-5.3-codex")
                                        }
                                    >
                                        <div className="flex items-center gap-1.5 text-xs font-medium w-full">
                                            <span>
                                                {t("chatInput.modelGpt52Codex")}
                                            </span>
                                            {forceModel === "gpt-5.3-codex" && (
                                                <Check className="h-3 w-3 ml-auto text-purple-600" />
                                            )}
                                        </div>
                                        <p className="text-[10px] text-gray-500">
                                            {t(
                                                "chatInput.modelGpt52CodexDescription"
                                            )}
                                        </p>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        className="flex flex-col items-start gap-1 p-2 cursor-pointer"
                                        onClick={() =>
                                            setForceModel &&
                                            setForceModel("gpt-5.4")
                                        }
                                    >
                                        <div className="flex items-center gap-1.5 text-xs font-medium w-full">
                                            <span>
                                                {t("chatInput.modelGpt52")}
                                            </span>
                                            {forceModel === "gpt-5.4" && (
                                                <Check className="h-3 w-3 ml-auto text-purple-600" />
                                            )}
                                        </div>
                                        <p className="text-[10px] text-gray-500">
                                            {t(
                                                "chatInput.modelGpt52Description"
                                            )}
                                        </p>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        className="flex flex-col items-start gap-1 p-2 cursor-pointer"
                                        onClick={() =>
                                            setForceModel &&
                                            setForceModel(
                                                "gemini-3.1-pro-preview"
                                            )
                                        }
                                    >
                                        <div className="flex items-center gap-1.5 text-xs font-medium w-full">
                                            <span>
                                                {t("chatInput.modelGemini3Pro")}
                                            </span>
                                            {forceModel ===
                                                "gemini-3.1-pro-preview" && (
                                                <Check className="h-3 w-3 ml-auto text-purple-600" />
                                            )}
                                        </div>
                                        <p className="text-[10px] text-gray-500">
                                            {t(
                                                "chatInput.modelGemini3ProDescription"
                                            )}
                                        </p>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        className="flex flex-col items-start gap-1 p-2 cursor-pointer"
                                        onClick={() =>
                                            setForceModel &&
                                            setForceModel("zai.glm-5")
                                        }
                                    >
                                        <div className="flex items-center gap-1.5 text-xs font-medium w-full">
                                            <span>
                                                {t("chatInput.modelGlm5")}
                                            </span>
                                            {forceModel === "zai.glm-5" && (
                                                <Check className="h-3 w-3 ml-auto text-purple-600" />
                                            )}
                                        </div>
                                        <p className="text-[10px] text-gray-500">
                                            {t(
                                                "chatInput.modelGlm5Description"
                                            )}
                                        </p>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuSub>
                                        <DropdownMenuSubTrigger className="flex items-center gap-1.5 text-xs p-2 cursor-pointer">
                                            <span>
                                                {t("chatInput.otherModels")}
                                            </span>
                                        </DropdownMenuSubTrigger>
                                        <DropdownMenuSubContent className="w-72">
                                            <DropdownMenuItem
                                                className="flex flex-col items-start gap-1 p-2 cursor-pointer"
                                                onClick={() =>
                                                    setForceModel &&
                                                    setForceModel(
                                                        "us.anthropic.claude-opus-4-6-v1"
                                                    )
                                                }
                                            >
                                                <div className="flex items-center gap-1.5 text-xs font-medium w-full">
                                                    <span>
                                                        {t(
                                                            "chatInput.modelClaudeOpus46"
                                                        )}
                                                    </span>
                                                    {forceModel ===
                                                        "us.anthropic.claude-opus-4-6-v1" && (
                                                        <Check className="h-3 w-3 ml-auto text-purple-600" />
                                                    )}
                                                </div>
                                                <p className="text-[10px] text-gray-500">
                                                    {t(
                                                        "chatInput.modelClaudeOpus46Description"
                                                    )}
                                                </p>
                                            </DropdownMenuItem>
                                            <DropdownMenuItem
                                                className="flex flex-col items-start gap-1 p-2 cursor-pointer"
                                                onClick={() =>
                                                    setForceModel &&
                                                    setForceModel(
                                                        "us.anthropic.claude-haiku-4-5-20251001-v1:0"
                                                    )
                                                }
                                            >
                                                <div className="flex items-center gap-1.5 text-xs font-medium w-full">
                                                    <span>
                                                        {t(
                                                            "chatInput.modelClaudeHaiku"
                                                        )}
                                                    </span>
                                                    {forceModel ===
                                                        "us.anthropic.claude-haiku-4-5-20251001-v1:0" && (
                                                        <Check className="h-3 w-3 ml-auto text-purple-600" />
                                                    )}
                                                </div>
                                                <p className="text-[10px] text-gray-500">
                                                    {t(
                                                        "chatInput.modelClaudeHaikuDescription"
                                                    )}
                                                </p>
                                            </DropdownMenuItem>
                                        </DropdownMenuSubContent>
                                    </DropdownMenuSub>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}

                        {/* Right side - Send/Pause Button */}
                        <div className="ml-auto flex-shrink-0">
                            {isGeneratingCode ? (
                                <Button
                                    onClick={cancelBackgroundTask}
                                    className="h-7 px-2 bg-blue-500 hover:bg-blue-600 text-white border-none shadow-none"
                                    title={t("chatInput.pauseGeneration")}
                                >
                                    <Square className="h-3.5 w-3.5 text-white" />
                                </Button>
                            ) : isTyping ? (
                                <Button
                                    onClick={cancelBackgroundTask}
                                    variant="ghost"
                                    className="h-7 px-2 border-none shadow-none"
                                    title={t("chatInput.pauseGeneration")}
                                >
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleSendMessage}
                                    disabled={
                                        disabled ||
                                        (!message?.trim() &&
                                            files?.length === 0 &&
                                            selectedSkills.length === 0) ||
                                        isTyping ||
                                        isLoadingFileUpload ||
                                        isCreatingWorkflow ||
                                        limitReached
                                    }
                                    className="h-7 px-2 bg-transparent hover:bg-transparent border-none shadow-none"
                                    title={
                                        disabled
                                            ? "This workflow is archived and cannot be used"
                                            : isCreatingWorkflow
                                            ? t("chatInput.creatingWorkflow")
                                            : t("chatInput.sendMessage")
                                    }
                                >
                                    {isLoadingFileUpload ||
                                    isCreatingWorkflow ? (
                                        <Loader2 className="h-4 w-4 text-gray-900 hover:text-blue-600 transition-colors animate-spin" />
                                    ) : (
                                        <Send className="h-4 w-4 text-gray-900 hover:text-blue-600 transition-colors" />
                                    )}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Hidden File Input */}
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                    accept="*/*"
                />

                {/* Drag Overlay */}
                <AnimatePresence>
                    {isDragOver && (
                        <motion.div
                            key="drag-overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.15 }}
                            className="absolute inset-0 bg-blue-100 bg-opacity-90 rounded-xl flex items-center justify-center pointer-events-none border-2 border-dashed border-blue-400 backdrop-blur-sm"
                        >
                            <motion.div
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.8, opacity: 0 }}
                                transition={{ duration: 0.2, ease: "easeOut" }}
                                className="flex flex-col items-center gap-3 text-blue-600"
                            >
                                <div className="relative">
                                    <motion.div
                                        animate={{ y: [0, -5, 0] }}
                                        transition={{
                                            duration: 1.5,
                                            repeat: Infinity,
                                            ease: "easeInOut",
                                        }}
                                    >
                                        <Paperclip className="h-12 w-12 text-blue-500" />
                                    </motion.div>
                                    <div className="absolute -top-1 -right-1 bg-blue-500 rounded-full p-1">
                                        <svg
                                            className="h-4 w-4 text-white"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 4v16m8-8H4"
                                            />
                                        </svg>
                                    </div>
                                </div>
                                <div className="text-center">
                                    <div className="font-semibold text-lg mb-1">
                                        {t("chatInput.dropFilesHereToUpload")}
                                    </div>
                                    <div className="text-sm text-blue-500">
                                        Drop your files here to upload
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>

            {/* Human Support Modals */}
            <HumanSupportTutorial
                isOpen={showHumanTutorial}
                onClose={() => setShowHumanTutorial(false)}
                onContinue={handleHumanTutorialContinue}
            />
            <HumanSupportPaywall
                isOpen={showHumanPaywall}
                onClose={() => setShowHumanPaywall(false)}
            />
        </>
    );
};

export default ChatInput;
